import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    IconButton,
    InputAdornment,
    Checkbox,
    FormControlLabel,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import resetFilter from "../../../assets/icons/reset.png";
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import BasicTable from '../../../components/Table';
import TableData from './table';
import ReservationQuickPopup from "./Popup/index";
import { fetchReservationList, fetchRoomTypes, getReportData, getUnallocatedAmount } from '../../../service/network';
import { useLoading } from '../../../context/loading.context';
import StyledTextField from '../../../components/UIComponents/StyledTextField';
import StyledAutocomplete from '../../../components/UIComponents/StyledAutocomplete';
import CustomCard from '../../../components/UIComponents/CustomCard';
import CustomButton from '../../../components/Buttons';
import HeaderComponent from '../../../components/Header';
import * as XLSX from 'xlsx';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useNavigate } from 'react-router-dom';
import moment from 'moment/moment';

export default function ReservationList() {
    const theme = useTheme();
    const { showLoader, hideLoader } = useLoading();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Filter options
    const reservationStatusOptions = ['Booked', 'Checked-In', 'Checked-Out', 'Cancelled'];
    const paymentStatusOptions = ['UnPaid', 'Completed'];
    const bookingSourceOptions = ['Website', 'Walk-In', 'Phone', 'Agent'];

    // State management
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [roomTypeOptions, setRoomTypeOptions] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [roomopts, setRoomOpts] = useState([]);
    const navigate = useNavigate()
    const [filters, setFilters] = useState({
        fromDate: moment().subtract(5, 'days').format('YYYY-MM-DD'),
        toDate: moment().add(5, 'days').format('YYYY-MM-DD'),
        fromcheckINDate: '', // Initial value for check-in date
        reservationStatus: '',
        roomType: '',
        paymentStatus: '',
        bookingSource: '',
        dueCheckIn: false,
        dueCheckOut: false,
        room_name: ''
    });
    useEffect(() => {
        getReportData({ doctype: "Room", fields: ["room_name"] }).then((resp) => {
            let details = [];
            if (resp.data.message && (!Array.isArray(resp.data.message))) {
                for (let opt of resp.data.message.values) {
                    details.push(opt[0]);
                }
            }
            setRoomOpts(details);
        }).catch((err) => {
            console.log(err)
        })
    }, []);


    // Table configuration
    const { formReservationListRows, ReservationListHeader } = TableData();

    // Get current date in YYYY-MM-DD format
    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    // Export to Excel function
    const exportToExcel = () => {
        // 1. Map reservations into an array of objects using Tamil labels as keys
        const exportData = reservations.map((reservation) => {
            const row = {};
            ReservationListHeader.forEach((header) => {
                row[header.label] = reservation[header.id]; // Tamil label as key
            });
            return row;
        });

        // 2. Generate Excel sheet
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Reservations");
        XLSX.writeFile(workbook, "reservations.xlsx");
    };


    // Initial data loading
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                showLoader();
                const roomTypeParams = {
                    doctype: 'Room Type',
                    fields: JSON.stringify(["name", "room_type"])
                };
                const roomTypeRes = await fetchRoomTypes(roomTypeParams);
                const roomTypes = roomTypeRes.data.message.values.map(item => item[1]);
                setRoomTypeOptions([...new Set(roomTypes)]);
                fetchReservations();
            } catch (err) {
                console.error("Failed to fetch initial data", err);
            } finally {
                hideLoader();
            }
        };

        loadInitialData();
    }, []);

    // Fetch reservations with filters
    const fetchReservations = async () => {
        try {
            showLoader(true);

            // Build filters array
            const filterConditions = [];

            // Date filters
            if (filters.fromDate) {
                filterConditions.push(['from_date', '<=', filters.toDate]);
            }
            if (filters.toDate) {
                filterConditions.push(['to_date', '>=', filters.fromDate]);
            }
            if (filters.fromcheckINDate) {
                filterConditions.push(['from_date', '=', filters.fromcheckINDate]);
            }
            // Due Check-In/Check-Out filters
            if (filters.dueCheckIn) {
                filterConditions.push(['from_date', '=', getCurrentDate()]);
            }
            if (filters.dueCheckOut) {
                filterConditions.push(['to_date', '=', getCurrentDate()]);
            }

            // Status filters
            if (filters.reservationStatus) {
                filterConditions.push(['status', '=', filters.reservationStatus]);
            }
            if (filters.roomType) {
                filterConditions.push(['room_type', '=', filters.roomType]);
            }
            if (filters.room_name) {
                filterConditions.push(['room_name', '=', filters.room_name]);
            }
            if (filters.paymentStatus) {
                filterConditions.push(['payment_status', '=', filters.paymentStatus]);
            }
            if (filters.bookingSource) {
                filterConditions.push(['booking_source', '=', filters.bookingSource]);
            }

            // If no filters are applied, show all data
            const params = {
                doctype: 'Reservation',
                fields: JSON.stringify([
                    "name", "primary_guest_name", "from_date", "to_date", "booking_source", "status", "booked_date",
                    "room_type", "room", "room_name", "no_of_child", "no_of_adult", "no_of_days", "payment_status", "paid_amount", "invoiced_amount"
                ]),
                ...(filterConditions.length > 0 && { filters: JSON.stringify(filterConditions) }),
            };

            // Add search if needed
            if (searchQuery) {
                params.query = searchQuery;
            }

            const response = await fetchReservationList(params);

            const transformedData = response.data.message.values.map(reservation => {
                const [
                    name, primary_guest_name, from_date, to_date, booking_source, status, booked_date,
                    room_type, room, room_name, no_of_child, no_of_adult, no_of_days, payment_status, paid_amount, invoiced_amount
                ] = reservation;

                return {
                    reservation_id: name,
                    guest_name: primary_guest_name,
                    arrival_date: from_date,
                    departure_date: to_date,
                    nights: no_of_days - 1,
                    room_type: room_type || "N/A",
                    room: room,
                    room_no: room_name || "N/A",
                    adult_child: `${no_of_adult} / ${no_of_child}`,
                    reservation_status: status,
                    booking_date: booked_date,
                    booking_source: booking_source || "N/A",
                    payment_status: payment_status,
                    paid_amount: paid_amount,
                    invoiced_amount: invoiced_amount,
                };
            });

            setReservations(transformedData);
            console.log("transformedData", transformedData)
        } catch (error) {
            console.error('Error fetching reservations:', error);
            setReservations([]);
        } finally {
            hideLoader();
        }
    };

    // Handle filter changes
    const handleFilterChange = (name, value) => {
        // If dueCheckIn or dueCheckOut is checked, uncheck the other one
        if (name === 'dueCheckIn' && value === true) {
            setFilters(prev => ({
                ...prev,
                dueCheckOut: false,
                [name]: value
            }));
        } else if (name === 'dueCheckOut' && value === true) {
            setFilters(prev => ({
                ...prev,
                dueCheckIn: false,
                [name]: value
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Reset all filters
    const resetFilters = () => {
        setFilters({
            fromDate: moment().subtract(5, 'days').format('YYYY-MM-DD'),
            toDate: moment().add(5, 'days').format('YYYY-MM-DD'),
            fromcheckINDate: '',
            reservationStatus: '',
            roomType: '',
            paymentStatus: '',
            bookingSource: '',
            dueCheckIn: false,
            dueCheckOut: false,
            room_name: ''
        });
        setSearchQuery('');
    };

    // Fetch data when filters change
    useEffect(() => {
        fetchReservations();
    }, [filters]);

    return (
        <Box sx={{
            width: '100%',
            p: { xs: 1, sm: 2 },
            boxSizing: 'border-box'
        }}>
            <HeaderComponent title="Reservation List" />

            <CustomCard
                bgColor="light"
                shadow="lg"
                borderRadius="xxl"
                sx={{
                    width: '100%',
                    minHeight: 800,
                    borderTopLeftRadius: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Main Content Container */}
                <Box sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: "space-around",
                    overflow: 'hidden',
                    width: '100%'
                }}>
                    {/* Search and Actions Row - Improved layout */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            mb: 3,
                            width: '100%',
                        }}
                    >
                        {/* Left Side: From and To Date */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Grid item>
                                <StyledTextField
                                    label="From Date"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.fromDate || ''}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <StyledTextField
                                    label="To Date"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.toDate || ''}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                />
                            </Grid>
                            <Grid item>
                                <StyledTextField
                                    label="Arrival Date"
                                    type="date"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    value={filters.fromcheckINDate || ''}
                                    onChange={(e) => handleFilterChange('fromcheckINDate', e.target.value)}
                                    InputProps={{
                                        placeholder: "YYYY-MM-DD", // Optional: Shows format hint when empty
                                    }}
                                />
                            </Grid>
                        </Box>

                        {/* Right Side: Action Buttons */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                            }}
                        >
                            <IconButton
                                color="primary"
                                sx={{
                                    border: '2px solid #27394D',
                                    borderRadius: '8px',
                                    p: '4px',
                                    minWidth: 40,
                                    height: 40,
                                    flexShrink: 0,
                                }}
                                onClick={fetchReservations}
                            >
                                <AutorenewOutlinedIcon fontSize="small"/>
                            </IconButton>

                            <CustomButton
                                variant="outlined"
                                startIcon={<FileUploadIcon fontSize="small" />}
                                sx={{
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    borderRadius: theme.borderRadius.medium,
                                    border: "2px solid var(--Primary-Blue-MB, #27394D)",
                                    textTransform: 'none',
                                    '&:hover': { borderColor: 'primary.dark' },
                                    px: 1.5,
                                    py: 0.75,
                                    minWidth: 'auto',
                                    hegiht:"36px",
                                    flexShrink: 0,
                                }}
                                onClick={exportToExcel}
                            >
                                Export
                            </CustomButton>

                            <IconButton
                                onClick={() => navigate('/reservation')}
                                sx={{
                                    backgroundColor: "lightblue.main",
                                    color: 'white',
                                    border: '3px solid var(--Primary-Blue-MB, #27394D)',
                                    borderRadius: '50%',
                                    width: 50,
                                    height: 50,
                                    '&:hover': {
                                        backgroundColor: theme.palette.light.main,
                                    }
                                }}
                            >
                                <AddRoundedIcon sx={{ color: theme.palette.primary.main, fontSize: 36 }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Filters Section */}
                    <Box sx={{
                        width: '100%',
                        mb: 3,
                        overflow: 'hidden'
                    }}>
                        <Grid container spacing={1}>
                            {/* Date Fields */}

                            {/* Status Filters */}
                            <Grid size={2}>
                                <StyledAutocomplete
                                    fullWidth
                                    label="Reservation Status"
                                    options={reservationStatusOptions}
                                    value={filters.reservationStatus}
                                    onChange={(e, newValue) => handleFilterChange('reservationStatus', newValue)}
                                />
                            </Grid>
                            <Grid size={2}>
                                <StyledAutocomplete
                                    fullWidth
                                    label="Room Type"
                                    options={roomTypeOptions}
                                    value={filters.roomType}
                                    onChange={(e, newValue) => handleFilterChange('roomType', newValue)}
                                />
                            </Grid>
                            <Grid size={2   }>
                                <StyledAutocomplete
                                    fullWidth
                                    label="Room"
                                    options={roomopts}
                                    value={filters.room_name}
                                    onChange={(e, newValue) => handleFilterChange('room_name', newValue)}
                                />
                            </Grid>
                            <Grid size={2}>
                                <StyledAutocomplete
                                    fullWidth
                                    label="Payment Status"
                                    options={paymentStatusOptions}
                                    value={filters.paymentStatus}
                                    onChange={(e, newValue) => handleFilterChange('paymentStatus', newValue)}
                                />
                            </Grid>
                            <Grid size={2}  >
                                <StyledAutocomplete
                                    fullWidth
                                    label="Booking Source"
                                    options={bookingSourceOptions}
                                    value={filters.bookingSource}
                                    onChange={(e, newValue) => handleFilterChange('bookingSource', newValue)}
                                />
                            </Grid>

                            {/* Checkbox Section */}
                            <Grid size={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {/* stack the two pairs */}
                                    <Box display="flex" flexDirection="column">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={filters.dueCheckIn}
                                                    onChange={(e) => handleFilterChange('dueCheckIn', e.target.checked)}
                                                    sx={{
                                                        '&.MuiCheckbox-root': {
                                                            color: theme.palette.primary.main, // unchecked border color
                                                        },
                                                        '&.Mui-checked': {
                                                            color: theme.palette.primary.main, // checked fill color
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Due Check-In"
                                            sx={{ mt: 1.8, whiteSpace: 'nowrap' }}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={filters.dueCheckOut}
                                                    onChange={(e) => handleFilterChange('dueCheckOut', e.target.checked)}
                                                    sx={{
                                                        '&.MuiCheckbox-root': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                        '&.Mui-checked': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Due Check-Out"
                                            sx={{ mt: -2, whiteSpace: 'nowrap' }}
                                        />
                                    </Box>



                                    {/* reset button */}
                                    <IconButton
                                        color="primary"
                                        sx={{
                                            border: '2px solid #27394D',
                                            borderRadius: '8px',
                                            p: '6px',
                                            mt: 1.5
                                        }}
                                        onClick={resetFilters}
                                    >
                                        <img src={resetFilter} width={22} height={22} alt="Reset filters" />
                                    </IconButton>
                                </Box>
                            </Grid>

                        </Grid>
                    </Box>


                    {/* Table Section */}
                    <Box sx={{
                        flex: 1,
                        width: '100%',
                        
                    }}>
                        <BasicTable
                            backgroundColor={'light.main'}
                            disableElevation={true}
                            data={{
                                rows: ReservationListHeader,
                                columns: formReservationListRows(reservations),
                            }}
                            sx={{
                                minWidth: 'max-content'
                            }}
                            onRowClick={(rowData) => {
                                setSelectedReservation(rowData.details);
                                setOpenPopup(true);
                            }}
                        />
                    </Box>
                </Box>

                {/* Reservation Popup */}
                <ReservationQuickPopup
                    open={openPopup}
                    onClose={() => {
                        setOpenPopup(false);
                        setSelectedReservation(null);
                    }}
                    reservation={selectedReservation}
                    refreshData={fetchReservations}
                />
            </CustomCard>
        </Box>
    );
}